import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { extractError } from './api';
import type { User, LoginRequest, AuthState } from '../types';

interface AuthContextValue extends AuthState {
  login: (credentials: LoginRequest, returnTo?: string) => Promise<void>;
  logout: () => void;
  updateAuthUser: (user: Partial<User>) => void;
  getToken: () => string | null;
  idleRemainingSec: number;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const AUTH_STORAGE_KEY = 'rotkreuz-auth';

interface PersistedAuth {
  state: AuthState;
}

function loadAuth(): AuthState {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed: PersistedAuth = JSON.parse(stored);
      if (parsed.state?.token) {
        return { ...parsed.state, isLoading: false };
      }
    }
  } catch {
    // ignore corrupt data
  }
  return { user: null, token: null, isAuthenticated: false, isLoading: false };
}

function persistAuth(state: AuthState): void {
  const payload: PersistedAuth = { state };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
}

/**
 * Nur interne Pfade als Login-Ziel akzeptieren (kein Open Redirect).
 */
export function sanitizeReturnTo(path?: string | null): string | null {
  if (!path || typeof path !== 'string') return null;
  if (!path.startsWith('/')) return null;
  // Nur app-interne Pfade: kein "//host", "://" oder Backslash.
  if (path.includes('//') || path.includes('://') || path.includes('\\')) return null;
  // Steuerzeichen koennten beim URL-Parsing ein "//host" erzeugen.
  if (/[\u0000-\u001F\u007F]/.test(path)) return null;
  // Die Login-Seite (auch mit Slash/Query/Hash) ist kein sinnvolles Ruecksprungziel.
  const pathname = path.split(/[?#]/, 1)[0];
  if (pathname === '/login' || pathname === '/login/') return null;
  return path;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(loadAuth);
  const navigate = useNavigate();

  const login = useCallback(
    async (credentials: LoginRequest, returnTo?: string) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await api.post<{ accessToken: string; user: User }>('/auth/login', credentials);
        const { accessToken: token, user } = response.data;
        const newState: AuthState = {
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        };
        setState(newState);
        persistAuth(newState);

        // Deep-Link (z.B. Direktlink eines Fragebogens) nach dem Login direkt öffnen.
        const target = sanitizeReturnTo(returnTo);
        if (target) {
          navigate(target, { replace: true });
          return;
        }

        switch (user.role) {
          case 'USER_INPUT':
          case 'USER_VIEWER':
            navigate('/my');
            break;
          case 'USER_EDITOR':
            navigate('/questionnaires');
            break;
          default:
            navigate('/');
        }
      } catch (error) {
        setState((prev) => ({ ...prev, isLoading: false }));
        const apiError = extractError(error);
        throw new Error(apiError.message);
      }
    },
    [navigate],
  );

  const logout = useCallback(() => {
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
    localStorage.removeItem(AUTH_STORAGE_KEY);
    navigate('/login');
  }, [navigate]);

  const updateAuthUser = useCallback((userData: Partial<User>) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const updated = { ...prev, user: { ...prev.user, ...userData } };
      persistAuth(updated);
      return updated;
    });
  }, []);

  const getToken = useCallback((): string | null => {
    return state.token;
  }, [state.token]);

  // Auto-redirect to login if not authenticated.
  // Public routes (no login required): the login page itself and anonymous
  // questionnaire links (/fill/<token>/anonymous).
  useEffect(() => {
    const path = window.location.pathname;
    const isPublicPath =
      path === '/login' || /^\/fill\/[^/]+\/anonymous\/?$/.test(path);
    if (!state.isAuthenticated && !state.isLoading && !isPublicPath) {
      // Ziel merken, damit nach dem Login direkt dorthin zurückgesprungen wird.
      const returnTo = path + window.location.search;
      navigate('/login', { state: { returnTo }, replace: true });
    }
  }, [state.isAuthenticated, state.isLoading, navigate]);

  // Idle-Timeout: Logout nach 30 Minuten Inaktivitaet
  const [idleRemainingSec, setIdleRemainingSec] = useState<number>(30 * 60);

  useEffect(() => {
    if (!state.isAuthenticated) return;
    const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
    let idleTimer: ReturnType<typeof setTimeout>;
    let countdownInterval: ReturnType<typeof setInterval>;

    function resetIdleTimer() {
      clearTimeout(idleTimer);
      clearInterval(countdownInterval);
      setIdleRemainingSec(30 * 60);

      idleTimer = setTimeout(() => {
        clearInterval(countdownInterval);
        logout();
      }, IDLE_TIMEOUT_MS);

      countdownInterval = setInterval(() => {
        setIdleRemainingSec((prev) => Math.max(0, prev - 1));
      }, 1000);
    }

    const events = ['mousedown', 'keydown', 'touchstart', 'mousemove', 'scroll', 'click'];
    events.forEach((event) => window.addEventListener(event, resetIdleTimer));
    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      clearInterval(countdownInterval);
      events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
    };
  }, [state.isAuthenticated, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      updateAuthUser,
      getToken,
      idleRemainingSec,
    }),
    [state, login, logout, updateAuthUser, getToken, idleRemainingSec],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
